import * as React from 'react';

import * as TogglePrimitive from '@radix-ui/react-toggle';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const toggleVariants = cva(
    'inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 cursor-pointer disabled:cursor-not-allowed',
    {
        variants: {
            variant: {
                default: 'bg-transparent',
                outline:
                    'border border-input bg-transparent rounded-sm hover:bg-accent/50 hover:text-accent-foreground data-[state=on]:bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] dark:data-[state=on]:bg-primary/10 data-[state=on]:border-primary data-[state=on]:text-foreground dark:data-[state=on]:text-accent-foreground',
            },
            size: {
                default: 'h-9 px-2 min-w-9',
                sm: 'h-8 px-1.5 min-w-8',
                lg: 'h-10 px-2.5 min-w-10',
            },
            // `rounded-2xl` wins over `variant="outline"`'s `rounded-sm` — ordered after it here,
            // and `cn()` resolves the conflict via tailwind-merge.
            shape: {
                default: '',
                pill: 'rounded-2xl',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
            shape: 'default',
        },
    },
);

const Toggle = React.forwardRef<
    React.ElementRef<typeof TogglePrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> &
        VariantProps<typeof toggleVariants>
>(({ className, variant, size, shape, ...props }, ref) => (
    <TogglePrimitive.Root
        ref={ref}
        tabIndex={0} // required for tabulation on new safari versions to work
        className={cn(toggleVariants({ variant, size, shape, className }))}
        {...props}
    />
));

Toggle.displayName = TogglePrimitive.Root.displayName;

export { Toggle, toggleVariants };
